/**
 * Studio installation: devices (`studio_connections`), ports and links
 * (docs/wiring-documentation-proposal.md, "1. Studio installation").
 *
 * Every mutator here bumps `studios.installation_version` once per affected
 * studio and writes one `studio_wiring_changes` row per changed entity, all
 * carrying the new version. A database trigger announces the version on
 * NOTIFY studio_installation_changed.
 *
 * On the server every mutator first locks the rows of the studios it touches
 * (`lockStudios`), so concurrent installation mutations of one studio run one
 * after the other: the link invariant checks see each other's links and every
 * mutation gets its own version.
 */
import { describeDomains } from "../operation-meta.js";
import { redactConnectionOptions } from "../../types/connections.js";
import { addStudioPortsInputSchema, createStudioConnectionInputSchema, createStudioLinkInputSchema, createStudioPortInputSchema, createStudioPortsFromTemplateInputSchema, deleteStudioConnectionInputSchema, deleteStudioLinkInputSchema, deleteStudioPortInputSchema, moveStudioConnectionInputSchema, setStudioLinkPatchStatusInputSchema, studioIDQueryArgsSchema, studioWiringChangesQueryArgsSchema, updateStudioConnectionInputSchema, updateStudioLinkInputSchema, updateStudioPortInputSchema, } from "../projects-input-schemas.js";
import { ConflictError, NotFoundError, ValidationError } from "../../errors.js";
import { assertAdminDataWriteAccess } from "../projects-helpers.js";
import { assertValidLink, planDeviceDelete, planDeviceMove, validateDeviceParent, deterministicUUID, } from "../../types/wiring.js";
import { buildGenericPorts, getDeviceTemplate, GENERIC_DEVICE_TEMPLATE_ID, missingTemplatePorts, portSlotKey, } from "../../types/device-templates.js";
import { effectiveConfigIDOf, initialPatchStatus, } from "../../types/effective-wiring.js";
function isStaff(ctx) {
    return ctx.role === "admin" || ctx.role === "employee";
}
export function createStudioInstallationQueries({ defineProjectQuery, projectZql, }) {
    const byStudio = (table, order) => defineProjectQuery(studioIDQueryArgsSchema, ({ args, ctx }) => {
        if (!isStaff(ctx)) {
            return projectZql[table].where("id", "__unauthorized__");
        }
        return projectZql[table]
            .where("studioID", args.studioID)
            .orderBy(order[0], order[1]);
    });
    return {
        studioConnections: {
            byStudio: byStudio("studioConnections", ["name", "asc"]),
        },
        studioPorts: {
            byStudio: byStudio("studioPorts", ["number", "asc"]),
        },
        studioLinks: {
            byStudio: byStudio("studioLinks", ["createdAt", "asc"]),
        },
        studioBridgeStatus: {
            byStudio: defineProjectQuery(studioIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!isStaff(ctx)) {
                    return projectZql.studioBridgeStatus
                        .where("studioID", "__unauthorized__")
                        .one();
                }
                return projectZql.studioBridgeStatus
                    .where("studioID", args.studioID)
                    .one();
            }),
        },
        studioWiringChanges: {
            byStudio: defineProjectQuery(studioWiringChangesQueryArgsSchema, ({ args, ctx, }) => {
                if (!isStaff(ctx)) {
                    return projectZql.studioWiringChanges.where("id", "__unauthorized__");
                }
                return projectZql.studioWiringChanges
                    .where("studioID", args.studioID)
                    .related("changedByPerson")
                    .orderBy("changedAt", "desc")
                    .limit(args.limit ?? STUDIO_WIRING_CHANGES_PAGE);
            }),
        },
    };
}
/** Default page size of the installation history (audit rows, newest first) */
export const STUDIO_WIRING_CHANGES_PAGE = 500;
/**
 * Server only: lock the studio rows (sorted, so two-studio moves cannot
 * deadlock) before anything is read. Concurrent installation mutations of the
 * same studio wait here until the first one commits, then read its changes.
 * The client runs optimistically against its local state and needs no lock.
 */
export async function lockStudios(tx, studioIDs) {
    if (tx.location !== "server")
        return;
    for (const studioID of [...new Set(studioIDs)].sort()) {
        await tx.dbTransaction.query("SELECT id FROM studios WHERE id = $1 FOR NO KEY UPDATE", [studioID]);
    }
}
/**
 * Increment `installation_version` and return the new value. On the server
 * the increment is atomic in SQL; the client increments its local copy (the
 * server's value replaces it on sync).
 */
async function bumpInstallationVersion(tx, projectZql, studioID) {
    if (tx.location === "server") {
        const [row] = [
            ...(await tx.dbTransaction.query("UPDATE studios SET installation_version = installation_version + 1 WHERE id = $1 RETURNING installation_version", [studioID])),
        ];
        if (!row)
            throw new NotFoundError("Studio not found");
        return Number(row.installation_version);
    }
    const studio = await tx.run(projectZql.studios.where("id", studioID).one());
    if (!studio)
        throw new NotFoundError("Studio not found");
    const version = Number(studio.installationVersion ?? 0) + 1;
    await tx.mutate.studios.update({
        id: studioID,
        installationVersion: version,
    });
    return version;
}
/** JSON snapshot of a row for the audit log (drops undefined values). */
function snapshot(row) {
    if (row === undefined || row === null)
        return null;
    const copy = JSON.parse(JSON.stringify(row));
    // Device options can hold logins; the change log keeps only that they changed.
    if (copy && typeof copy === "object" && "options" in copy) {
        copy.options = redactConnectionOptions(copy.options);
    }
    return copy;
}
/**
 * Collects the changes of one mutation and writes them with the bumped
 * installation version of each studio involved.
 */
export function createChangeLog(tx, ctx, projectZql) {
    const entries = [];
    const studios = new Set();
    return {
        touch(studioID) {
            studios.add(studioID);
        },
        add(studioID, entity, entityID, op, before, after) {
            studios.add(studioID);
            entries.push({
                studioID,
                entity,
                entityID,
                op,
                before: snapshot(before),
                after: snapshot(after),
            });
        },
        async commit() {
            const now = Date.now();
            for (const studioID of studios) {
                const version = await bumpInstallationVersion(tx, projectZql, studioID);
                for (const [index, entry] of entries.entries()) {
                    if (entry.studioID !== studioID)
                        continue;
                    await tx.mutate.studioWiringChanges.insert({
                        id: deterministicUUID("studio-wiring-change", studioID, String(version), String(index), entry.entity, entry.entityID, entry.op),
                        studioID,
                        version,
                        changedBy: ctx.personID ?? null,
                        changedAt: now,
                        entity: entry.entity,
                        entityID: entry.entityID,
                        op: entry.op,
                        before: entry.before,
                        after: entry.after,
                    });
                }
            }
        },
    };
}
export async function loadInstallation(tx, projectZql, studioID) {
    const devices = await tx.run(projectZql.studioConnections.where("studioID", studioID));
    const ports = await tx.run(projectZql.studioPorts.where("studioID", studioID));
    const links = await tx.run(projectZql.studioLinks.where("studioID", studioID));
    return {
        devices: [...(devices ?? [])],
        ports: [...(ports ?? [])],
        links: [...(links ?? [])],
    };
}
/**
 * The studio's effective config: its active config, or the original of the
 * active scratch copy. Its temporary gear and re-patches are in effect.
 */
export async function studioEffectiveConfigID(tx, projectZql, studioID) {
    const studio = await tx.run(projectZql.studios.where("id", studioID).one());
    if (!studio?.activeConfigID)
        return null;
    const config = await tx.run(projectZql.studioConfigs.where("id", studio.activeConfigID).one());
    return config ? effectiveConfigIDOf(config) : studio.activeConfigID;
}
/**
 * The config that owns new temporary gear or a re-patch: the given config,
 * or the original of a scratch copy (gear added while a scratch copy runs
 * is physical reality, not an experiment).
 */
async function scopeConfigID(tx, projectZql, studioID, configID) {
    const config = await tx.run(projectZql.studioConfigs.where("id", configID).one());
    if (!config || config.studioID !== studioID) {
        throw new NotFoundError("Config not found in this studio");
    }
    return effectiveConfigIDOf(config) ?? config.id;
}
function assertParent(device, parentID, devices) {
    const error = validateDeviceParent(device, parentID, devices);
    if (error)
        throw new ValidationError(error);
}
/** Software addresses ports through its PC's cards; it never owns ports. */
function assertCanOwnPorts(device) {
    if (device.kind === "software") {
        throw new ValidationError(`${device.name ?? "This device"} is software and has no ports of its own. Add the ports to the card or PC it runs on.`);
    }
}
function assertFreeSlot(port, deviceID, ports, ignorePortID) {
    const key = portSlotKey(port);
    const clash = ports.find((other) => other.deviceID === deviceID &&
        other.id !== ignorePortID &&
        portSlotKey({
            direction: other.direction,
            group: other.group ?? null,
            number: other.number,
        }) === key);
    if (clash) {
        throw new ConflictError(`The device already has ${port.direction} ${port.group ? `${port.group} ` : ""}${port.number}`);
    }
}
export function createStudioInstallationMutators({ defineProjectMutator, projectZql, }) {
    async function insertPorts(tx, log, studioID, deviceID, ports, existingIDs = []) {
        const now = Date.now();
        const taken = new Set(existingIDs);
        for (const port of ports) {
            const id = port.id ?? templatePortID(deviceID, port, taken);
            taken.add(id);
            const row = {
                id,
                studioID,
                deviceID,
                direction: port.direction,
                signal: port.signal,
                number: port.number,
                name: port.name ?? null,
                group: port.group ?? null,
                control: port.control ?? null,
                createdAt: now,
                updatedAt: now,
            };
            await tx.mutate.studioPorts.insert(row);
            log.add(studioID, "port", row.id, "create", null, row);
        }
    }
    /**
     * Port ID derived from the device and the slot, so the client's optimistic
     * rows and the server's rows match. A port renumbered away from its slot
     * keeps its ID; the next free suffix is used then.
     */
    function templatePortID(deviceID, port, taken) {
        for (let attempt = 0;; attempt++) {
            const id = deterministicUUID("studio-port", deviceID, portSlotKey(port), String(attempt));
            if (!taken.has(id))
                return id;
        }
    }
    async function loadDevice(tx, id, studioID) {
        const device = await tx.run(projectZql.studioConnections.where("id", id).one());
        if (!device || device.studioID !== studioID) {
            throw new NotFoundError("Device not found");
        }
        return device;
    }
    return {
        studioConnections: {
            create: defineProjectMutator({
                input: createStudioConnectionInputSchema,
                description: "Add a device to a studio: hardware or software, optionally inside a parent device (a card in a PC, software on a PC). With configID it is temporary gear of that config, connected only while the config is active. Use studioPorts.createFromTemplate to give it ports.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const configID = args.configID
                    ? await scopeConfigID(tx, projectZql, args.studioID, args.configID)
                    : null;
                if (args.parentID) {
                    const { devices } = await loadInstallation(tx, projectZql, args.studioID);
                    assertParent({ id: args.id, studioID: args.studioID }, args.parentID, devices);
                    const parent = devices.find((d) => d.id === args.parentID);
                    if (parent?.configID && parent.configID !== configID) {
                        throw new ValidationError(`${parent.name} is temporary gear of a config; devices inside it belong to the same config`);
                    }
                }
                const now = Date.now();
                const row = {
                    id: args.id,
                    studioID: args.studioID,
                    name: args.name,
                    connectionType: args.connectionType,
                    host: args.host || null,
                    port: args.port ?? null,
                    model: args.model ?? null,
                    managementURL: args.managementURL ?? null,
                    enabled: args.enabled ?? true,
                    kind: args.kind ?? "hardware",
                    parentID: args.parentID ?? null,
                    templateID: args.templateID ?? null,
                    position: args.position ?? null,
                    notes: args.notes ?? null,
                    danteName: args.danteName || null,
                    embeddedAudio: args.embeddedAudio || null,
                    configID,
                    options: args.options ?? null,
                    createdAt: now,
                    updatedAt: now,
                };
                await tx.mutate.studioConnections.insert(row);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "device", row.id, "create", null, row);
                await log.commit();
            }),
            update: defineProjectMutator(updateStudioConnectionInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const current = await loadDevice(tx, args.id, args.studioID);
                if (args.kind === "software" && current.kind !== "software") {
                    const ports = await tx.run(projectZql.studioPorts.where("deviceID", args.id));
                    if ((ports ?? []).length > 0) {
                        throw new ConflictError("Software has no ports of its own. Delete this device's ports (or move them to its PC or card) before making it software.");
                    }
                }
                if (args.parentID !== undefined && args.parentID !== null) {
                    const { devices } = await loadInstallation(tx, projectZql, args.studioID);
                    assertParent(current, args.parentID, devices);
                    const parent = devices.find((d) => d.id === args.parentID);
                    if (parent?.configID &&
                        parent.configID !== (current.configID ?? null)) {
                        throw new ValidationError(`${parent.name} is temporary gear of a config; devices inside it belong to the same config`);
                    }
                }
                const updates = { id: args.id };
                for (const key of [
                    "name",
                    "connectionType",
                    "enabled",
                    "kind",
                ]) {
                    if (args[key] !== undefined)
                        updates[key] = args[key];
                }
                for (const key of [
                    "port",
                    "model",
                    "managementURL",
                    "parentID",
                    "templateID",
                    "position",
                    "notes",
                    "options",
                ]) {
                    if (args[key] !== undefined)
                        updates[key] = args[key] ?? null;
                }
                for (const key of ["danteName", "embeddedAudio"]) {
                    if (args[key] !== undefined)
                        updates[key] = args[key] || null;
                }
                if (args.host !== undefined)
                    updates.host = args.host || null;
                updates.updatedAt = Date.now();
                await tx.mutate.studioConnections.update(updates);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "device", args.id, "update", current, {
                    ...current,
                    ...updates,
                });
                await log.commit();
            }),
            delete: defineProjectMutator({
                input: deleteStudioConnectionInputSchema,
                description: "Delete a device together with its child devices, all their ports and every link touching those ports.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                await loadDevice(tx, args.id, args.studioID);
                const installation = await loadInstallation(tx, projectZql, args.studioID);
                const plan = planDeviceDelete(args.id, installation.devices, installation.ports, installation.links);
                const log = createChangeLog(tx, ctx, projectZql);
                for (const link of plan.links) {
                    await tx.mutate.studioLinks.delete({ id: link.id });
                    log.add(args.studioID, "link", link.id, "delete", link, null);
                }
                const portSet = new Set(plan.portIDs);
                for (const port of installation.ports) {
                    if (!portSet.has(port.id))
                        continue;
                    await tx.mutate.studioPorts.delete({ id: port.id });
                    log.add(args.studioID, "port", port.id, "delete", port, null);
                }
                // Leaves first, so no child points at a deleted parent
                for (const deviceID of [...plan.deviceIDs].reverse()) {
                    const device = installation.devices.find((d) => d.id === deviceID);
                    await tx.mutate.studioConnections.delete({ id: deviceID });
                    log.add(args.studioID, "device", deviceID, "delete", device, null);
                }
                // Configs keep the device's state: it is the device's own,
                // index-keyed state, and a load warns about devices that are gone
                // (validateConfigReferences). Rewriting them would also break the
                // lock of the active config.
                await log.commit();
            }),
            move: defineProjectMutator({
                input: moveStudioConnectionInputSchema,
                description: "Move a device (with its child devices and ports) to another studio. Its links to devices that stay behind are deleted.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                if (args.targetStudioID === args.studioID) {
                    throw new ValidationError("The device is already in this studio");
                }
                await lockStudios(tx, [args.studioID, args.targetStudioID]);
                await loadDevice(tx, args.id, args.studioID);
                const target = await tx.run(projectZql.studios.where("id", args.targetStudioID).one());
                if (!target || target.deletedAt) {
                    throw new NotFoundError("Target studio not found");
                }
                const installation = await loadInstallation(tx, projectZql, args.studioID);
                const plan = planDeviceMove(args.id, installation.devices, installation.ports, installation.links);
                const scoped = installation.devices.find((d) => plan.deviceIDs.includes(d.id) && d.configID);
                if (scoped) {
                    throw new ValidationError(`${scoped.name} is temporary gear of a config of this studio and can't move to another studio. Copy the config there instead.`);
                }
                const log = createChangeLog(tx, ctx, projectZql);
                const now = Date.now();
                const from = args.studioID;
                const to = args.targetStudioID;
                for (const link of plan.linksToDelete) {
                    await tx.mutate.studioLinks.delete({ id: link.id });
                    log.add(from, "link", link.id, "delete", link, null);
                }
                for (const deviceID of plan.deviceIDs) {
                    const device = installation.devices.find((d) => d.id === deviceID);
                    const updates = {
                        id: deviceID,
                        studioID: to,
                        // The root leaves its parent behind; children keep theirs
                        ...(deviceID === args.id ? { parentID: null } : {}),
                        updatedAt: now,
                    };
                    await tx.mutate.studioConnections.update(updates);
                    const after = { ...device, ...updates };
                    log.add(from, "device", deviceID, "move_out", device, after);
                    log.add(to, "device", deviceID, "move_in", device, after);
                }
                const portSet = new Set(plan.portIDs);
                for (const port of installation.ports) {
                    if (!portSet.has(port.id))
                        continue;
                    await tx.mutate.studioPorts.update({ id: port.id, studioID: to });
                    const after = { ...port, studioID: to };
                    log.add(from, "port", port.id, "move_out", port, after);
                    log.add(to, "port", port.id, "move_in", port, after);
                }
                for (const link of plan.linksToMove) {
                    await tx.mutate.studioLinks.update({ id: link.id, studioID: to });
                    const after = { ...link, studioID: to };
                    log.add(from, "link", link.id, "move_out", link, after);
                    log.add(to, "link", link.id, "move_in", link, after);
                }
                await log.commit();
            }),
        },
        studioPorts: {
            create: defineProjectMutator({
                input: createStudioPortInputSchema,
                description: "Add a port (physical connector) to a device. Numbers are 1-based as printed on the device.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                assertCanOwnPorts(await loadDevice(tx, args.deviceID, args.studioID));
                const { ports } = await loadInstallation(tx, projectZql, args.studioID);
                const port = {
                    id: args.id,
                    direction: args.direction,
                    signal: args.signal,
                    number: args.number,
                    name: args.name ?? null,
                    group: args.group ?? null,
                    control: args.control ?? null,
                };
                assertFreeSlot(port, args.deviceID, ports);
                const log = createChangeLog(tx, ctx, projectZql);
                await insertPorts(tx, log, args.studioID, args.deviceID, [port]);
                await log.commit();
            }),
            update: defineProjectMutator(updateStudioPortInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const current = await tx.run(projectZql.studioPorts.where("id", args.id).one());
                if (!current || current.studioID !== args.studioID) {
                    throw new NotFoundError("Port not found");
                }
                const next = {
                    ...current,
                    ...(args.direction !== undefined
                        ? { direction: args.direction }
                        : {}),
                    ...(args.signal !== undefined ? { signal: args.signal } : {}),
                    ...(args.number !== undefined ? { number: args.number } : {}),
                    ...(args.name !== undefined ? { name: args.name ?? null } : {}),
                    ...(args.group !== undefined ? { group: args.group ?? null } : {}),
                    ...(args.control !== undefined
                        ? { control: args.control ?? null }
                        : {}),
                };
                const { ports, links } = await loadInstallation(tx, projectZql, args.studioID);
                assertFreeSlot(next, current.deviceID, ports, current.id);
                if (next.direction !== current.direction) {
                    const portsAfter = ports.map((port) => port.id === next.id ? next : port);
                    for (const link of links) {
                        if (link.fromPortID === next.id || link.toPortID === next.id) {
                            assertValidLink(link, portsAfter, links);
                        }
                    }
                }
                const updates = {
                    id: args.id,
                    direction: next.direction,
                    signal: next.signal,
                    number: next.number,
                    name: next.name,
                    group: next.group,
                    control: next.control,
                    updatedAt: Date.now(),
                };
                await tx.mutate.studioPorts.update(updates);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "port", args.id, "update", current, {
                    ...current,
                    ...updates,
                });
                await log.commit();
            }),
            delete: defineProjectMutator({
                input: deleteStudioPortInputSchema,
                description: "Delete a port and the link on it, if any.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const { ports, links } = await loadInstallation(tx, projectZql, args.studioID);
                const port = ports.find((p) => p.id === args.id);
                if (!port)
                    throw new NotFoundError("Port not found");
                const log = createChangeLog(tx, ctx, projectZql);
                for (const link of links) {
                    if (link.fromPortID !== port.id && link.toPortID !== port.id) {
                        continue;
                    }
                    await tx.mutate.studioLinks.delete({ id: link.id });
                    log.add(args.studioID, "link", link.id, "delete", link, null);
                }
                await tx.mutate.studioPorts.delete({ id: port.id });
                log.add(args.studioID, "port", port.id, "delete", port, null);
                await log.commit();
            }),
            createFromTemplate: defineProjectMutator({
                input: createStudioPortsFromTemplateInputSchema,
                description: "Create a device's ports from a device template (default: the device's own template) or a generic N inputs / M outputs list. Existing ports are kept; only missing numbers are added.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const device = await loadDevice(tx, args.deviceID, args.studioID);
                assertCanOwnPorts(device);
                let templateID;
                let template;
                if (args.generic) {
                    templateID = GENERIC_DEVICE_TEMPLATE_ID;
                    template = buildGenericPorts(args.generic);
                }
                else {
                    templateID = args.templateID ?? device.templateID;
                    const found = getDeviceTemplate(templateID);
                    if (!found) {
                        throw new ValidationError(templateID
                            ? `Unknown device template: ${templateID}`
                            : "The device has no device template");
                    }
                    template = found.ports;
                }
                const ports = await tx.run(projectZql.studioPorts.where("deviceID", args.deviceID));
                const missing = missingTemplatePorts(template, ports ?? []);
                const log = createChangeLog(tx, ctx, projectZql);
                await insertPorts(tx, log, args.studioID, args.deviceID, missing, (ports ?? []).map((port) => port.id));
                if (!device.templateID) {
                    const updates = {
                        id: device.id,
                        templateID,
                        updatedAt: Date.now(),
                    };
                    await tx.mutate.studioConnections.update(updates);
                    log.add(args.studioID, "device", device.id, "update", device, {
                        ...device,
                        ...updates,
                    });
                }
                if (missing.length > 0 || !device.templateID)
                    await log.commit();
            }),
            addMany: defineProjectMutator({
                input: addStudioPortsInputSchema,
                description: "Add several ports to one device at once (e.g. ports a running device reports but the documentation lacks).",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                assertCanOwnPorts(await loadDevice(tx, args.deviceID, args.studioID));
                const existing = [
                    ...((await tx.run(projectZql.studioPorts.where("deviceID", args.deviceID))) ?? []),
                ];
                const ports = args.ports.map((port) => ({
                    ...port,
                    name: port.name ?? null,
                    group: port.group ?? null,
                    control: port.control ?? null,
                }));
                const existingIDs = existing.map((port) => port.id);
                for (const port of ports) {
                    assertFreeSlot(port, args.deviceID, existing);
                    existing.push({
                        ...port,
                        id: port.id ?? "",
                        deviceID: args.deviceID,
                    });
                }
                const log = createChangeLog(tx, ctx, projectZql);
                await insertPorts(tx, log, args.studioID, args.deviceID, ports, existingIDs);
                await log.commit();
            }),
        },
        studioLinks: {
            create: defineProjectMutator({
                input: createStudioLinkInputSchema,
                description: "Document a cable from an output (or bidirectional) port to an input (or bidirectional) port of the same studio. Each port can be in only one link. With configID it is a re-patch of that config (or the cable of its temporary gear): in effect only while the config is, and it suspends the permanent links on its ports meanwhile.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const { devices, ports, links } = await loadInstallation(tx, projectZql, args.studioID);
                const configID = args.configID
                    ? await scopeConfigID(tx, projectZql, args.studioID, args.configID)
                    : null;
                const patchStatus = configID
                    ? initialPatchStatus(configID, await studioEffectiveConfigID(tx, projectZql, args.studioID))
                    : null;
                const now = Date.now();
                const row = {
                    id: args.id,
                    studioID: args.studioID,
                    fromPortID: args.fromPortID,
                    toPortID: args.toPortID,
                    configID,
                    cable: args.cable ?? null,
                    patchPoint: args.patchPoint ?? null,
                    patchStatus,
                    notes: args.notes ?? null,
                    verifiedAt: args.verifiedAt ?? null,
                    verifiedBy: args.verifiedBy ?? null,
                    createdAt: now,
                    updatedAt: now,
                };
                assertValidLink(row, ports, links, devices);
                await tx.mutate.studioLinks.insert(row);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "link", row.id, "create", null, row);
                await log.commit();
            }),
            update: defineProjectMutator(updateStudioLinkInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const { devices, ports, links } = await loadInstallation(tx, projectZql, args.studioID);
                const current = links.find((link) => link.id === args.id);
                if (!current)
                    throw new NotFoundError("Link not found");
                const updates = { id: args.id };
                if (args.fromPortID !== undefined) {
                    updates.fromPortID = args.fromPortID;
                }
                if (args.toPortID !== undefined)
                    updates.toPortID = args.toPortID;
                for (const key of [
                    "cable",
                    "patchPoint",
                    "notes",
                    "verifiedAt",
                    "verifiedBy",
                ]) {
                    if (args[key] !== undefined)
                        updates[key] = args[key] ?? null;
                }
                const next = { ...current, ...updates };
                if (next.fromPortID !== current.fromPortID ||
                    next.toPortID !== current.toPortID) {
                    assertValidLink(next, ports, links, devices);
                }
                updates.updatedAt = Date.now();
                await tx.mutate.studioLinks.update(updates);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "link", args.id, "update", current, {
                    ...current,
                    ...updates,
                });
                await log.commit();
            }),
            delete: defineProjectMutator({
                input: deleteStudioLinkInputSchema,
                description: "Delete a documented cable (link).",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const current = await tx.run(projectZql.studioLinks.where("id", args.id).one());
                if (!current || current.studioID !== args.studioID) {
                    throw new NotFoundError("Link not found");
                }
                await tx.mutate.studioLinks.delete({ id: args.id });
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "link", args.id, "delete", current, null);
                await log.commit();
            }),
            setPatchStatus: defineProjectMutator({
                input: setStudioLinkPatchStatusInputSchema,
                description: "Tick a re-patch off on the patch sheet: patched (set up), restored, or back to pending set-up / pending restore. Loading never waits for these; they only tell whether the cables match the effective wiring.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await lockStudios(tx, [args.studioID]);
                const current = await tx.run(projectZql.studioLinks.where("id", args.id).one());
                if (!current || current.studioID !== args.studioID) {
                    throw new NotFoundError("Link not found");
                }
                if (!current.configID) {
                    throw new ValidationError("Only re-patches of a config have a patch status");
                }
                if (current.patchStatus === args.patchStatus)
                    return;
                const updates = {
                    id: args.id,
                    patchStatus: args.patchStatus,
                    updatedAt: Date.now(),
                };
                await tx.mutate.studioLinks.update(updates);
                const log = createChangeLog(tx, ctx, projectZql);
                log.add(args.studioID, "link", args.id, "update", current, {
                    ...current,
                    ...updates,
                });
                await log.commit();
            }),
        },
    };
}
export const studioInstallationDomainDescriptions = describeDomains({
    studioConnections: "Devices of a studio (hardware and software, nested by parent), the bridge drives those with a driver",
    studioPorts: "Physical connectors (ports) of studio devices, 1-based",
    studioLinks: "Documented cables between two ports of a studio; with a config they are that config's re-patches (patch sheet status)",
    studioWiringChanges: "Audit log of studio installation changes (devices, ports, links)",
    studioBridgeStatus: "Last heartbeat of a studio's bridge (loaded config, version, unsaved changes, devices); pairing is admin-only via /api/bridge-pairing",
});
